import { Events } from '@platform/client';
import { DatePickerWithTimezone, Dropdown, DropdownItem, Input, Modal } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { Event } from 'lib/client/src/platformapi.schemas';
import { useState } from 'react';

interface UpdateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  page?: number;
  limit?: number;
}

export const UpdateEventModal = (props: UpdateEventModalProps) => {
  const { event, isOpen, onClose, page, limit } = props;

  const [title, setTitle] = useState(event.title);
  const [hostedBy, setHostedBy] = useState(event.hostedBy);
  const [description, setDescription] = useState(event.description);
  const [startTime, setStartTime] = useState(new Date(event.startTime));
  const [endTime, setEndTime] = useState(new Date(event.endTime));
  const [link, setLink] = useState(event.link || '');
  const [timezone, setTimezone] = useState(event.timezone || '');

  const queryClient = useQueryClient();

  const submitEvent = Events.useUpdateEvent({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: Events.getGetEventsAdminQueryKey({
            limit: limit || 0,
            page: page || 0,
          }),
        });
      },
    },
  });

  const updateEvent = () => {
    submitEvent.mutate({
      data: {
        id: event.id,
        title,
        description,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        timezone,
        hostedBy,
        link,
      },
    });

    closeModal();
  };

  const closeModal = () => {
    onClose();
    setTitle('');
    setDescription('');
    setEndTime(new Date());
    setStartTime(new Date());
    setLink('');
    setTimezone('America/Los_Angeles');
  };

  const updateTimezone = (tz: string) => {
    setTimezone(tz);
    setStartTime(new Date(startTime.toLocaleString('en-US', { timeZone: tz })));
    setEndTime(new Date(endTime.toLocaleString('en-US', { timeZone: tz })));
  };

  return (
    <Modal
      variant='dialog'
      title='Update Event'
      isOpen={isOpen}
      headerDivision='divider'
      ignoreExtraPadding={true}
      onClose={closeModal}
      primaryCTA={{
        text: 'Update Event',
        callback: updateEvent,
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: closeModal,
      }}
    >
      <div className='md:w-[460px]'>
        <div className='flex flex-col gap-4 w-full'>
          <Input
            label='Title'
            type='inline'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            label='Hosted By'
            type='inline'
            value={hostedBy}
            onChange={(e) => setHostedBy(e.target.value)}
          />
          <Input
            label='Description'
            type='multiline'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input
            label='Link'
            type='inline'
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          <DatePickerWithTimezone
            label={'Start Time'}
            includeTime={true}
            date={startTime}
            setDate={(newDate) => setStartTime(newDate)}
          />
          <DatePickerWithTimezone
            label={'End Time'}
            includeTime={true}
            date={endTime}
            setDate={(newDate) => setEndTime(newDate)}
          />
          <Dropdown
            type='button'
            ariaLabel='Timezone Dropdown'
            buttonProps={{
              variant: 'secondary-gray',
              size: 'lg',
              children: <div className='p-4'>{timezone ? timezone : 'Select a Timezone'}</div>,
            }}
          >
            {Intl.supportedValuesOf('timeZone').map((tz) => {
              return (
                <DropdownItem key={tz} onAction={() => updateTimezone(tz)}>
                  {tz}
                </DropdownItem>
              );
            })}
          </Dropdown>
        </div>
      </div>
    </Modal>
  );
};
